import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Form, Input, Modal, Radio, Select, Space, Switch, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import {
  EditOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PlusOutlined,
  SafetyOutlined,
  SearchOutlined,
  StopOutlined,
  SyncOutlined,
  UnlockOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { checkNewEmailStatus } from '../api/users'
import ManageUsersModal from '../components/ManageUsersModal'
import { useToast } from '../contexts/ToastContext'
import { UserManagementProvider } from '../contexts/UserManagementContext'
import { useUserManagementContext } from '../contexts/useUserManagementContext'
import {
  useBulkInviteUsersMutation,
  useCheckUserExistsMutation,
  useCreateUserMutation,
  useDeleteUserMutation,
  useInviteUserMutation,
  useRecreateUserMutation,
  useReinviteUserMutation,
  useResetUserAuthenticatorMutation,
  useRevokeUserMutation,
  useUnlockUserMutation,
  useUpdateOrg2FAMutation,
  useUpdateUser2FAMutation,
  useUpdateUserMutation,
  useUserDetail,
} from '../hooks/useUsers'
import { formatPhone, normalizePhone } from '../lib/phone'
import { personNameSchema, requiredEmailSchema, zodAntdValidator } from '../lib/validation'
import type { UserFormValues, UserRoleLabel, UserRow, UserStatusLabel } from '../types/users'
import { updateSession, useAuthSession } from '../lib/session'
import { getAllowedEnvOptions } from '../lib/environment'
import { UserTypes } from '../types/authorization'

type ModalState = {
  open: boolean
  mode: 'create' | 'edit'
  userId: string
}

type FormValues = {
  firstName: string
  lastName: string
  phone: string
  email: string
  typeId: number
  statusId: number
  groupIds: string[]
  reportIds: string[]
  allowedEnv?: string
}

type EmailStatus = 'free' | 'exists' | 'pending' | 'deleted'

type PendingEmailConfirmation = {
  values: FormValues
}

const userTypeOptions = [
  { label: 'Admin', value: 2 },
  { label: 'User', value: 3 },
]

const statusOptions = [
  { label: 'Active', value: 1 },
  { label: 'Inactive', value: 2 },
]

const userRoleMap: Record<number, UserRoleLabel> = {
  1: 'SuperAdmin',
  2: 'Admin',
  3: 'User',
  4: 'User',
  5: 'Owner',
  7: 'Admin',
}

const firstNameSchema = personNameSchema('First Name')
const lastNameSchema = personNameSchema('Last Name')
const emailSchema = requiredEmailSchema('Email')

function statusTag(status: UserStatusLabel) {
  if (status === 'Active') return <Tag color="green">Active</Tag>
  if (status === 'Inactive') return <Tag>Inactive</Tag>
  if (status === 'Invited') return <Tag color="cyan">Invited</Tag>
  if (status === 'Expired') return <Tag color="volcano">Expired</Tag>
  return <Tag color="gold">For Invite</Tag>
}

function UsersContent() {
  const authSession = useAuthSession()
  const { addToast } = useToast()
  const userManagement = useUserManagementContext()

  const defaultPageSize =
    authSession?.defaultPageSize && authSession.defaultPageSize > 0 ? authSession.defaultPageSize : 25
  const [pagination, setPagination] = useState({ current: 1, pageSize: defaultPageSize })
  const [modalState, setModalState] = useState<ModalState>({ open: false, mode: 'create', userId: '' })
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [manageUsersOpen, setManageUsersOpen] = useState(false)
  const [bulkInviteSent, setBulkInviteSent] = useState(false)
  const [bulkSelectedUserIds, setBulkSelectedUserIds] = useState<string[]>([])
  const [initialEmail, setInitialEmail] = useState('')
  const [initialFormValues, setInitialFormValues] = useState<FormValues | null>(null)
  const [hasEmailUpdate, setHasEmailUpdate] = useState<boolean | null>(null)
  const [isLocked, setIsLocked] = useState(false)
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('free')
  const [existingEmailText, setExistingEmailText] = useState('')
  const [pendingEmailConfirmation, setPendingEmailConfirmation] = useState<PendingEmailConfirmation | null>(null)

  const [form] = Form.useForm<FormValues>()

  const rows = userManagement.users
  const groupOptions = userManagement.groupOptions
  const reportOptions = userManagement.reportOptions
  const isDiAdmin = authSession?.currentUserTypeId === UserTypes.Admin
  const canManageOrg2FA =
    authSession?.currentUserTypeId === UserTypes.Admin ||
    authSession?.currentUserTypeId === UserTypes.ClientAdmin
  const allowedEnvOptions = useMemo(() => getAllowedEnvOptions(), [])
  const bulkOptions = useMemo(
    () =>
      rows
        .filter((user) => user.statusId === 7)
        .map((user) => ({
          label: user.fullName || user.email,
          value: user.id,
        })),
    [rows]
  )
  const bulkOptionIds = useMemo(() => bulkOptions.map((option) => option.value), [bulkOptions])
  const allBulkSelected = bulkOptionIds.length > 0 && bulkSelectedUserIds.length === bulkOptionIds.length
  const someBulkSelected = bulkSelectedUserIds.length > 0 && !allBulkSelected
  const isEmailLockedForPendingVerification = modalState.mode === 'edit' && hasEmailUpdate === true

  const { data: editDetail, isFetching: isDetailFetching } = useUserDetail(
    modalState.mode === 'edit' ? modalState.userId : ''
  )
  const canEditStatus = modalState.mode === 'edit' && (editDetail?.statusId === 1 || editDetail?.statusId === 2)

  const createMutation = useCreateUserMutation()
  const recreateMutation = useRecreateUserMutation()
  const updateMutation = useUpdateUserMutation()
  const deleteMutation = useDeleteUserMutation()
  const bulkInviteMutation = useBulkInviteUsersMutation()
  const reinviteMutation = useReinviteUserMutation()
  const inviteMutation = useInviteUserMutation()
  const revokeMutation = useRevokeUserMutation()
  const unlockMutation = useUnlockUserMutation()
  const resetAuthenticatorMutation = useResetUserAuthenticatorMutation()
  const update2faMutation = useUpdateUser2FAMutation()
  const updateOrg2faMutation = useUpdateOrg2FAMutation()
  const checkExistsMutation = useCheckUserExistsMutation()

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [userManagement.userSearch, userManagement.hideInactiveUsers])

  useEffect(() => {
    if (modalState.mode !== 'edit' || !editDetail) return

    form.setFieldsValue({
      firstName: editDetail.firstName,
      lastName: editDetail.lastName,
      phone: editDetail.phone,
      email: editDetail.email,
      typeId: editDetail.typeId || 3,
      statusId: editDetail.statusId || 1,
      groupIds: editDetail.groupIds || [],
      reportIds: editDetail.reportIds || [],
      allowedEnv: editDetail.allowedEnv || undefined,
    })
    setInitialEmail(editDetail.email)
    setInitialFormValues({
      firstName: editDetail.firstName,
      lastName: editDetail.lastName,
      phone: editDetail.phone,
      email: editDetail.email,
      typeId: editDetail.typeId || 3,
      statusId: editDetail.statusId || 1,
      groupIds: editDetail.groupIds || [],
      reportIds: editDetail.reportIds || [],
      allowedEnv: editDetail.allowedEnv || undefined,
    })
    setIsLocked(Boolean(editDetail.isLocked))
  }, [editDetail, modalState.mode, form])

  const openCreate = () => {
    form.setFieldsValue({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      typeId: 2,
      statusId: 1,
      groupIds: [],
      reportIds: [],
      allowedEnv: undefined,
    })
    setInitialEmail('')
    setInitialFormValues({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      typeId: 2,
      statusId: 1,
      groupIds: [],
      reportIds: [],
      allowedEnv: undefined,
    })
    setHasEmailUpdate(null)
    setIsLocked(false)
    setEmailStatus('free')
    setExistingEmailText('')
    setModalState({ open: true, mode: 'create', userId: '' })
  }

  const openEdit = useCallback((row: UserRow) => {
    setModalState({ open: true, mode: 'edit', userId: row.id })
    setInitialEmail(row.email)
    setIsLocked(row.isLocked)
    setEmailStatus('free')
    setExistingEmailText('')
  }, [])

  useEffect(() => {
    if (!modalState.open || modalState.mode !== 'edit' || !initialEmail) return

    let cancelled = false
    setHasEmailUpdate(null)

    void checkNewEmailStatus(initialEmail)
      .then((status) => {
        if (!cancelled) {
          setHasEmailUpdate(Boolean(status?.hasEmailUpdate))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasEmailUpdate(null)
        }
      })

    return () => {
      cancelled = true
    }
  }, [initialEmail, modalState.mode, modalState.open])

  const closeModal = () => {
    setModalState({ open: false, mode: 'create', userId: '' })
    form.resetFields()
    setInitialEmail('')
    setInitialFormValues(null)
    setHasEmailUpdate(null)
    setIsLocked(false)
    setEmailStatus('free')
    setExistingEmailText('')
    setPendingEmailConfirmation(null)
  }

  const handleStatusAction = useCallback(async (user: UserRow, action: 'reinvite' | 'invite' | 'revoke') => {
    try {
      if (action === 'reinvite') await reinviteMutation.mutateAsync(user.id)
      if (action === 'invite') await inviteMutation.mutateAsync(user.id)
      if (action === 'revoke') await revokeMutation.mutateAsync(user.id)
      addToast(`${action === 'revoke' ? 'Revoke' : 'Invite'} sent`, 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Action failed', 'error')
    }
  }, [addToast, inviteMutation, reinviteMutation, revokeMutation])

  const unlock = useCallback(async (row: UserRow) => {
    try {
      const response = await unlockMutation.mutateAsync(row.id)
      if (response?.code === 'ResetPasswordAlreadySent') {
        addToast('Password reset email already sent to this user', 'warning')
      } else {
        addToast('Password reset email sent to user', 'success')
      }
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unlock failed', 'error')
    }
  }, [addToast, unlockMutation])

  // Lost-phone recovery: the user falls back to the email link and is asked to set the app up again.
  const resetAuthenticator = useCallback((row: UserRow) => {
    Modal.confirm({
      title: 'Reset authenticator app',
      content: `${row.email} will get an email link when they sign in, and will be asked to set up their authenticator app again.`,
      okText: 'Reset',
      okType: 'danger',
      onOk: async () => {
        try {
          const response = await resetAuthenticatorMutation.mutateAsync(row.id)
          addToast(
            response?.reset ? 'Authenticator app reset' : "This user hasn't set up an authenticator app",
            response?.reset ? 'success' : 'info'
          )
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Reset failed', 'error')
        }
      },
    })
  }, [addToast, resetAuthenticatorMutation])

  const toggle2FA = useCallback(async (row: UserRow, checked: boolean) => {
    if (!authSession?.currentClientId) return
    try {
      await update2faMutation.mutateAsync({
        UserId: row.id,
        DefaultClientId: authSession.currentClientId,
        // Explicit, so a double click can't flip it back.
        IsTwoFactorLogin: checked,
      })
      addToast(`2FA ${checked ? 'enabled' : 'disabled'}`, 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to update 2FA', 'error')
    }
  }, [addToast, authSession?.currentClientId, update2faMutation])

  const confirmOrg2FA = useCallback((next: boolean) => {
    const clientId = authSession?.currentClientId
    if (!clientId) return
    Modal.confirm({
      title: next ? 'Enable 2FA for All Users?' : 'Disable 2FA for All Users?',
      content: 'This changes two-factor authentication for every user in this organisation.',
      okText: next ? 'Enable' : 'Disable',
      onOk: async () => {
        try {
          await updateOrg2faMutation.mutateAsync({ isTwoFactorLogin: next, clientId })
          addToast(`2FA ${next ? 'enabled' : 'disabled'} for all users`, 'success')
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Unable to update organisation 2FA', 'error')
        }
      },
    })
  }, [addToast, authSession?.currentClientId, updateOrg2faMutation])

  const processUpdate = async (payload: UserFormValues, email: string, values: FormValues) => {
    const detail = await updateMutation.mutateAsync(payload)
    const changedEmail = email !== initialEmail
    const notifEmail = authSession?.currentUserTypeId === UserTypes.Admin ? email : initialEmail
    const normalizedCurrent = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      phone: normalizePhone(values.phone),
      email: '',
      typeId: values.typeId,
      statusId: values.statusId,
      groupIds: values.groupIds || [],
      reportIds: values.reportIds || [],
    }
    const normalizedInitial = initialFormValues
      ? {
          firstName: initialFormValues.firstName.trim(),
          lastName: initialFormValues.lastName.trim(),
          phone: normalizePhone(initialFormValues.phone),
          email: '',
          typeId: initialFormValues.typeId,
          statusId: initialFormValues.statusId,
          groupIds: initialFormValues.groupIds || [],
          reportIds: initialFormValues.reportIds || [],
        }
      : null
    const hasOtherChanges = normalizedInitial ? JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedInitial) : true

    if (detail?.success && changedEmail && values.statusId === 1 && !isLocked) {
      if (detail?.hasChangedEmail) {
        addToast(`A verification email has been sent to ${notifEmail}.`, 'success')
      } else {
        addToast(`A verification email has already been sent to ${notifEmail}.`, 'info')
      }
    } else if (detail?.success && changedEmail && values.statusId === 2) {
      addToast(
        hasOtherChanges
          ? 'Changes saved. Email update not applied - user must be Active.'
          : 'Email update not applied - user must be Active.',
        'warning'
      )
    } else if (detail?.success && changedEmail && isLocked) {
      addToast(
        hasOtherChanges
          ? 'Changes saved. Email update not applied - user must be Unlocked.'
          : 'Email update not applied - user must be Unlocked.',
        'warning'
      )
    } else if (detail?.success) {
      addToast('User updated', 'success')
    } else {
      addToast('Unable to update user', 'error')
      return
    }

    closeModal()
  }

  const confirmPendingEmailUpdate = async () => {
    if (!pendingEmailConfirmation) return

    try {
      await saveUser(pendingEmailConfirmation.values, true)
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to save user', 'error')
    }
  }

  const saveUser = async (providedValues?: FormValues, skipEmailChangeWarning = false) => {
    try {
      const values = providedValues ?? await form.validateFields()
      const normalizedPhone = normalizePhone(values.phone)
      if (normalizedPhone.length !== 10) {
        addToast('Phone number must be exactly 10 digits.', 'error')
        return
      }

      const email = values.email.trim()
      const shouldConfirmEmailChange =
        modalState.mode === 'edit' &&
        email !== initialEmail &&
        values.statusId === 1 &&
        hasEmailUpdate === false &&
        !isLocked

      if (shouldConfirmEmailChange && !skipEmailChangeWarning) {
        setPendingEmailConfirmation({ values })
        return
      }

      let pendingUserId: string | undefined
      let canRecreate = false
      let userOverride = false

      if (modalState.mode === 'create' || email !== initialEmail) {
        const existsState = await checkExistsMutation.mutateAsync(email)
        userOverride = Boolean(existsState?.userOverride)
        canRecreate = Boolean(existsState?.deleted || userOverride)
        pendingUserId = typeof existsState?.pending === 'string' ? existsState.pending : undefined
        if (existsState?.deleted) setEmailStatus('deleted')
        else if (pendingUserId) setEmailStatus('pending')
        else if (existsState?.exists && !existsState?.userOverride) {
          setEmailStatus('exists')
          setExistingEmailText(email)
        } else {
          setEmailStatus('free')
          setExistingEmailText('')
        }

        if (existsState?.exists && !existsState?.userOverride) {
          addToast('User already exists. Please use another email.', 'error')
          return
        }
      }

      const payload: UserFormValues = {
        userId: modalState.mode === 'edit' ? (pendingUserId || modalState.userId) : pendingUserId,
        clientId: authSession?.currentClientId,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        phone: normalizedPhone,
        email,
        typeId: values.typeId,
        statusId: modalState.mode === 'edit'
          ? (canEditStatus ? values.statusId : (editDetail?.statusId ?? values.statusId))
          : values.statusId,
        groupIds: values.groupIds || [],
        reportIds: values.reportIds || [],
        groupId: values.groupIds?.[0] || '',
        isrlc: false,
        userOverride,
        role: userRoleMap[values.typeId] || 'User',
        defaultClientId: modalState.mode === 'edit'
          ? (editDetail?.defaultClientId || authSession?.currentClientId || '')
          : (authSession?.currentClientId || ''),
        groups: values.groupIds
          .map((groupId) => groupOptions.find((group) => group.value === groupId)?.label || '')
          .filter(Boolean),
        defaultPageSize: authSession?.defaultPageSize || 10,
        isTwoFactorLogin: modalState.mode === 'edit' ? Boolean(editDetail?.isTwoFactorLogin) : false,
        allowedEnv: isDiAdmin
          ? (values.allowedEnv || '')
          : (editDetail?.allowedEnv || ''),
      }

      if (modalState.mode === 'create') {
        if (canRecreate) await recreateMutation.mutateAsync(payload)
        else await createMutation.mutateAsync(payload)
        addToast('User created', 'success')
        closeModal()
        return
      }

      await processUpdate(payload, email, values)
    } catch (e) {
      if (typeof e === 'object' && e !== null && 'errorFields' in e) return
      addToast(e instanceof Error ? e.message : 'Unable to save user', 'error')
    }
  }

  const deleteRow = async () => {
    if (modalState.mode !== 'edit' || !modalState.userId) return
    Modal.confirm({
      title: 'Delete user',
      content: 'Are you sure you want to delete this user?',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(modalState.userId)
          await userManagement.refetchUsers()
          addToast('User deleted successfully', 'success')
          closeModal()
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Unable to delete user', 'error')
          throw e
        }
      },
    })
  }

  const onTableChange = (nextPagination: TablePaginationConfig) => {
    const nextPageSize = nextPagination.pageSize ?? pagination.pageSize
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPageSize,
    }))
    if (nextPageSize !== authSession?.defaultPageSize) {
      updateSession({ defaultPageSize: nextPageSize })
    }
  }

  const openBulkInviteModal = () => {
    setBulkSelectedUserIds([])
    setBulkInviteSent(false)
    setBulkModalOpen(true)
  }

  const openManageUsersModal = () => {
    setManageUsersOpen(true)
  }

  const closeManageUsersModal = () => {
    setManageUsersOpen(false)
  }

  const toggleSelectAllBulkUsers = (checked: boolean) => {
    setBulkSelectedUserIds(checked ? bulkOptionIds : [])
  }

  const sendBulkInvite = async () => {
    try {
      await bulkInviteMutation.mutateAsync(bulkSelectedUserIds)
      setBulkInviteSent(true)
      addToast('Invite emails sent', 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Failed to send bulk invite', 'error')
    }
  }

  const columns: ColumnsType<UserRow> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'fullName',
        key: 'fullName',
        sorter: (a, b) => a.fullName.localeCompare(b.fullName),
        render: (_, row) => (
          <button
            type="button"
            onClick={() => void openEdit(row)}
            className="font-medium text-gray-900 hover:text-brand-600 text-left"
          >
            {row.fullName}
          </button>
        ),
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        render: (v: string) => formatPhone(v),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'User Type',
        key: 'role',
        render: (_, row) => row.role,
      },
      {
        title: 'User Groups',
        key: 'groups',
        render: (_, row) => (row.groups.length ? row.groups.join(', ') : '—'),
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, row) => statusTag(row.status),
      },
      {
        title: '2FA',
        key: 'twofa',
        render: (_, row) => (
          <Switch
            checked={row.twofa}
            size="small"
            onChange={(checked) => void toggle2FA(row, checked)}
            loading={update2faMutation.isPending}
          />
        ),
      },
      {
        title: 'Locked',
        key: 'locked',
        render: (_, row) => (row.isLocked ? <Tag color="volcano">Locked</Tag> : <Tag color="green">Unlocked</Tag>),
      },
      {
        title: 'Last Login',
        dataIndex: 'lastLogin',
        key: 'lastLogin',
        render: (value: string | null) =>
          value
            ? new Date(value).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })
            : '—',
      },
      {
        title: 'Actions',
        key: 'action',
        width: 170,
        render: (_, row) => (
          <Space size={2}>
            {(row.statusId === 6 || row.statusId === 7) && (
              <Tooltip title={row.statusId === 7 ? 'Invite' : 'Reinvite'}>
                <Button
                  type="text"
                  icon={<MailOutlined />}
                  onClick={() => void handleStatusAction(row, row.statusId === 7 ? 'invite' : 'reinvite')}
                />
              </Tooltip>
            )}
            {row.statusId === 5 && (
              <Tooltip title="Revoke">
                <Button type="text" icon={<StopOutlined />} onClick={() => void handleStatusAction(row, 'revoke')} />
              </Tooltip>
            )}
            {row.isLocked && (
              <Tooltip title="Unlock">
                <Button type="text" icon={<UnlockOutlined />} onClick={() => void unlock(row)} />
              </Tooltip>
            )}
            {canManageOrg2FA && row.twofa && (
              <Tooltip title="Reset authenticator app">
                <Button type="text" icon={<SafetyOutlined />} onClick={() => resetAuthenticator(row)} />
              </Tooltip>
            )}
            <Tooltip title="Edit user">
              <Button type="text" icon={<EditOutlined />} onClick={() => void openEdit(row)} />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [canManageOrg2FA, handleStatusAction, openEdit, resetAuthenticator, toggle2FA, unlock, update2faMutation.isPending]
  )

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white to-indigo-50/35">
      <div className="px-6 pt-5 pb-4 border-b border-brand-100">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-sm text-gray-500 mt-1">Client access and user lifecycle operations</p>
          </div>
          <Tag color="cyan-inverse">{rows.length} users</Tag>
        </div>
      </div>
      <div className="p-4 bg-white/70">
        <Space wrap>
          <Button icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
          <Button icon={<MailOutlined />} onClick={openBulkInviteModal}>
            Bulk Invite
          </Button>
          <Button icon={<UploadOutlined />} onClick={openManageUsersModal}>
            Manage Users
          </Button>
          <Input
            value={userManagement.userSearch}
            onChange={(e) => userManagement.setUserSearch(e.target.value)}
            placeholder="Search users"
            suffix={<SearchOutlined />}
            allowClear
            style={{ width: 220 }}
          />
          <Checkbox
            checked={userManagement.hideInactiveUsers}
            onChange={(e) => userManagement.setHideInactiveUsers(e.target.checked)}
          >
            Hide Inactive Users
          </Checkbox>
          {canManageOrg2FA && (
            <Checkbox
              data-testid="org-2fa-checkbox"
              checked={userManagement.org2FA}
              disabled={!authSession?.currentClientId || updateOrg2faMutation.isPending}
              onChange={(e) => confirmOrg2FA(e.target.checked)}
            >
              Enable 2FA for All Users
            </Checkbox>
          )}
          <Tooltip title="Refresh table">
            <Button icon={<SyncOutlined />} onClick={() => void userManagement.refetchUsers()} />
          </Tooltip>
          {(userManagement.usersLoading || userManagement.usersFetching) && (
            <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
              <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              Loading...
            </span>
          )}
        </Space>
      </div>
      <div className="p-4">
        {userManagement.usersError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={(userManagement.usersErrorObject as Error)?.message || 'Failed to load users'}
          />
        )}
        <Table<UserRow>
          rowKey="id"
          loading={userManagement.usersLoading || userManagement.usersFetching}
          columns={columns}
          dataSource={rows}
          onChange={onTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: rows.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </div>

      <Modal
        open={modalState.open}
        title={modalState.mode === 'create' ? 'Add New User' : 'Edit User'}
        onCancel={closeModal}
        onOk={() => void saveUser()}
        okText={modalState.mode === 'create' ? 'Create' : 'Update'}
        okButtonProps={{
          loading:
            createMutation.isPending ||
            recreateMutation.isPending ||
            updateMutation.isPending ||
            checkExistsMutation.isPending,
        }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space>
            {modalState.mode === 'edit' && (
              <Button
                danger
                icon={<StopOutlined />}
                loading={deleteMutation.isPending}
                onClick={() => void deleteRow()}
                disabled={authSession?.currentUserId === modalState.userId}
              >
                Delete
              </Button>
            )}
            <CancelBtn />
            <OkBtn />
          </Space>
        )}
      >
        <Form form={form} layout="vertical">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[
                { validator: zodAntdValidator(firstNameSchema) },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[
                { validator: zodAntdValidator(lastNameSchema) },
              ]}
            >
              <Input />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                { required: true, message: 'Phone is required.' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve()
                    return normalizePhone(value).length === 10
                      ? Promise.resolve()
                      : Promise.reject(new Error('Phone number must be exactly 10 digits.'))
                  },
                },
              ]}
              getValueFromEvent={(event) => normalizePhone(event?.target?.value).slice(0, 10)}
              getValueProps={(value) => ({ value: formatPhone(value) === '—' ? '' : formatPhone(value) })}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="email"
              label={(
                <span className="inline-flex items-center gap-1">
                  <span>Email</span>
                  {isEmailLockedForPendingVerification && (
                    <Tooltip title="A verification email has already been sent. Email can be updated again after 24 hours.">
                      <InfoCircleOutlined className="text-red-500" />
                    </Tooltip>
                  )}
                </span>
              )}
              rules={[
                { validator: zodAntdValidator(emailSchema) },
              ]}
            >
              <Input disabled={isEmailLockedForPendingVerification} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item name="typeId" label="Role" rules={[{ required: true, message: 'Role is required.' }]}>
              <Radio.Group options={userTypeOptions} />
            </Form.Item>
            {canEditStatus && (
              <Form.Item name="statusId" label="Status" rules={[{ required: true, message: 'Status is required.' }]}>
                <Radio.Group options={statusOptions} />
              </Form.Item>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Form.Item name="groupIds" label="User Groups">
              <Select
                mode="multiple"
                allowClear
                placeholder="Select user groups"
                options={groupOptions}
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item name="reportIds" label="Direct Reports">
              <Select
                mode="multiple"
                allowClear
                placeholder="Select direct reports"
                options={reportOptions}
                optionFilterProp="label"
              />
            </Form.Item>
          </div>

          {isDiAdmin && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Form.Item name="allowedEnv" label="Environment Access">
                <Select
                  allowClear
                  placeholder="Select environment access"
                  options={allowedEnvOptions}
                />
              </Form.Item>
            </div>
          )}

          {emailStatus === 'pending' && <Alert type="info" showIcon message="User is pending creation." />}
          {emailStatus === 'deleted' && (
            <Alert
              type="info"
              showIcon
              message="A deleted user exists for this email and will be restored when saved."
            />
          )}
          {emailStatus === 'exists' && (
            <Alert type="error" showIcon message={`User already exists for email: ${existingEmailText}`} />
          )}
          {isDetailFetching && modalState.mode === 'edit' && (
            <Alert type="info" showIcon message="Loading user details..." />
          )}
          {isLocked && (
            <Alert
              type="warning"
              showIcon
              message="User is currently locked. Email updates are restricted until unlocked."
            />
          )}
        </Form>
      </Modal>

      <ManageUsersModal
        open={manageUsersOpen}
        clientId={userManagement.clientId}
        onClose={closeManageUsersModal}
        onCompleted={() => {
          void userManagement.refetchUsers()
        }}
      />

      <Modal
        open={Boolean(pendingEmailConfirmation)}
        title="Update Email"
        onCancel={() => setPendingEmailConfirmation(null)}
        onOk={() => void confirmPendingEmailUpdate()}
        okText="Continue"
        confirmLoading={updateMutation.isPending}
        destroyOnHidden
      >
        {pendingEmailConfirmation && (
          <p>
            If you proceed with changing this email from {initialEmail} to {pendingEmailConfirmation.values.email.trim()},
            you will not be able to make any email related changes for the next 24 hours. Continue?
          </p>
        )}
      </Modal>

      <Modal
        open={bulkModalOpen}
        title="Bulk Invite"
        onCancel={() => setBulkModalOpen(false)}
        onOk={() => void (bulkInviteSent ? setBulkModalOpen(false) : sendBulkInvite())}
        okText={bulkInviteSent ? 'OK' : 'Send Invite'}
        okButtonProps={{
          loading: bulkInviteMutation.isPending,
          disabled: !bulkInviteSent && bulkSelectedUserIds.length === 0,
        }}
        cancelButtonProps={{ style: { display: bulkInviteSent ? 'none' : undefined } }}
      >
        {!bulkInviteSent ? (
          <Form layout="vertical">
            <Form.Item label="Users">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Checkbox
                  checked={allBulkSelected}
                  indeterminate={someBulkSelected}
                  disabled={bulkOptions.length === 0}
                  onChange={(e) => toggleSelectAllBulkUsers(e.target.checked)}
                >
                  Select all For Invite users
                </Checkbox>
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={bulkOptions.length ? 'Select users' : 'No For Invite users available'}
                  options={bulkOptions}
                  value={bulkSelectedUserIds}
                  onChange={setBulkSelectedUserIds}
                  optionFilterProp="label"
                  disabled={bulkOptions.length === 0}
                />
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Alert type="success" showIcon message="Emails have been sent to the selected users." />
        )}
      </Modal>
    </div>
  )
}

export default function Users() {
  return (
    <UserManagementProvider>
      <UsersContent />
    </UserManagementProvider>
  )
}
